<template>
  <div class="baidu-map">
    <!-- 搜索框在"导航"表单项里，别让敲关键字去校验 location -->
    <el-input v-model="keywords" placeholder="输入名称或地址后回车搜索" clearable :validate-event="false" @keyup.enter="search">
      <template #append>
        <el-button @click="search">搜索</el-button>
      </template>
    </el-input>
    <div class="baidu-map-container" ref="container">
    </div>
    <div class="baidu-map-status" :class="{picked: !!value}">
      <template v-if="mapError">{{ mapError }}</template>
      <template v-else-if="value">已定位：{{ address || '地址解析中…' }}（{{ value }}）</template>
      <template v-else>尚未定位：搜索后点击结果标记，或直接在地图上点击选点</template>
    </div>
  </div>
</template>
<script>
import {inject, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {formItemContextKey} from 'element-plus'

export default {
  emits: ['update:value', 'update:preset'],
  props: {
    value: {type: String},
    preset: {type: Array}
  },
  setup(props, {emit}) {
    const keywords = ref('')
    const container = ref()
    const address = ref('')
    const mapError = ref('')
    const formItem = inject(formItemContextKey, null)
    let map, marker, localSearch, geocoder
    let searchPois = []
    // 自己 emit 出去的值会经父组件绕回 props.value，靠它区分"用户刚点的"和"外部载入的"
    let lastEmitted = null
    let geocodeSeq = 0

    const parseValue = (value) => {
      const [lat, lng] = (value || '').split(',').map(parseFloat)
      return Number.isFinite(lat) && Number.isFinite(lng) ? new BMapGL.Point(lng, lat) : null
    }

    const placeMarker = (point) => {
      marker && map.removeOverlay(marker)
      marker = new BMapGL.Marker(point)
      map.addOverlay(marker)
    }

    // 逆地理：回填地址，并把省市区交给父组件（景区没有区域下拉，division 全靠这里）
    const reverseGeocode = (point) => {
      const seq = ++geocodeSeq
      address.value = ''
      geocoder.getLocation(point, (result) => {
        if (seq !== geocodeSeq || !result) return
        const {province, city, district} = result.addressComponents
        address.value = result.address
        emit('update:preset', [province, city, district])
      })
    }

    const pick = (point) => {
      map.closeInfoWindow()
      localSearch.clearResults()
      searchPois = []
      placeMarker(point)
      lastEmitted = `${point.lat},${point.lng}`
      emit('update:value', lastEmitted)
      reverseGeocode(point)
      // 提交失败后留下的"必须填写"要跟着选点消掉
      nextTick(() => formItem?.validate?.('change').catch(() => {}))
    }

    const centerOnPreset = () => {
      const preset = (props.preset || []).filter(Boolean)
      if (!preset.length) return
      geocoder.getPoint(preset.join(''), (point) => {
        point && !marker && map.centerAndZoom(point, 12)
      }, preset[1] || preset[0])
    }

    const showValue = (value) => {
      const point = parseValue(value)
      if (!point) {
        marker && map.removeOverlay(marker)
        marker = null
        address.value = ''
        centerOnPreset()
        return
      }
      placeMarker(point)
      map.centerAndZoom(point, 16)
      reverseGeocode(point)
    }

    const onMapClick = (e) => {
      if (e.overlay) {
        // 点在搜索结果的标记上：取该 POI 的精确坐标；点在其它覆盖物（已选标记、信息窗）上不处理
        const poi = searchPois.find(p => p.marker === e.overlay)
        poi && pick(poi.point)
        return
      }
      pick(new BMapGL.Point(e.latlng.lng, e.latlng.lat))
    }

    const search = () => {
      const text = keywords.value.trim()
      text && localSearch && localSearch.search(text)
    }

    onMounted(() => {
      if (typeof BMapGL === 'undefined') {
        mapError.value = '百度地图加载失败，请检查网络后刷新页面'
        return
      }
      map = new BMapGL.Map(container.value)
      map.centerAndZoom(new BMapGL.Point(116.404, 39.928), 12)
      map.enableScrollWheelZoom(true)
      geocoder = new BMapGL.Geocoder()
      localSearch = new BMapGL.LocalSearch(map, {
        renderOptions: {map},
        onMarkersSet: (pois) => searchPois = pois || []
      })
      map.addEventListener('click', onMapClick)
      showValue(props.value)
    })

    onBeforeUnmount(() => {
      map && map.destroy()
      map = null
    })

    // 编辑页的数据是异步拉回来的，组件挂载时 value 还是空的
    watch(() => props.value, (value) => {
      if (map && value !== lastEmitted) showValue(value)
    })

    // 酒店页先选了省市区、还没选点时，把地图挪过去
    watch(() => (props.preset || []).join(','), () => {
      map && !marker && centerOnPreset()
    })

    return {
      container,
      keywords,
      address,
      mapError,
      search
    }
  }
}
</script>
<style>
.baidu-map-container {
  width: 500px;
  height: 500px;
}

.baidu-map-status {
  width: 500px;
  line-height: 1.6;
  margin-top: 6px;
  color: #e6a23c;
}

.baidu-map-status.picked {
  color: #67c23a;
}
</style>
